import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, map, of, tap } from 'rxjs';
import { form, FormField, FormRoot, submit } from '@angular/forms/signals';
import { KeycloakAuthService } from '@dss-libs/shared/data-access/auth';
import { ClientService } from '@dss-libs/shared/data-access/clients';
import { IClient, EventTypeEnum, LocalstorageConstants } from '@dss-libs/shared/type/common-types';
import { SnackbarService } from '@dss-libs/shared/utility/common-services';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonComponent, JunoInput } from '@dss-libs/shared/ui/common-components';
import { MatIcon } from '@angular/material/icon';
import { ICreateOrganizationModel, createOrganizationSchema } from '@dss-libs/portal/type/portal-types';

@Component({
  selector: 'dss-libs-feature-create-organization',
  imports: [TranslocoDirective, ButtonComponent, JunoInput, MatIcon, FormRoot, FormField],
  templateUrl: './create-organization.component.html',
  styleUrl: './create-organization.component.css',
})
export class CreateOrganizationComponent {
  readonly #router = inject(Router);
  readonly #clientService = inject(ClientService);
  readonly #snackBarService = inject(SnackbarService);
  readonly #keycloakAuthService = inject(KeycloakAuthService);

  trustBadges = ['EIDAS QES', 'GDPR', 'SOC 2 II', 'SSL'];

  readonly createOrganizationModel = signal<ICreateOrganizationModel>({ name: '' });

  protected readonly createOrganizationForm = form(this.createOrganizationModel, createOrganizationSchema, {
    submission: {
      action: (field) =>
        firstValueFrom(
          this.#clientService.createClient(field().value().name).pipe(
            tap((client) => this.navigateToInviteMembers(client)),
            map(() => undefined),
            catchError(() => {
              this.#snackBarService.showToaster(EventTypeEnum.ERROR, 'CREATE_ORGANIZATION.CREATE_ERROR');
              return of(undefined);
            })
          )
        ),
    },
  });

  readonly isSubmitting = computed(() => this.createOrganizationForm().submitting());
  readonly isSubmitDisabled = computed(() => this.isSubmitting() || this.createOrganizationForm().invalid());

  submit(): Promise<boolean> {
    return submit(this.createOrganizationForm);
  }

  navigateToInviteMembers(client: IClient): void {
    if (client.id != null) {
      window.localStorage.setItem(LocalstorageConstants.CLIENT_ID, client.id);
      void this.#router.navigate(['/createClient/invite-members'], { replaceUrl: true });
    }
  }

  goBack() {
    void this.#keycloakAuthService.logout();
  }
}
